import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, thumbUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useFavourites } from "@/contexts/FavouritesContext";
import { DiscountBox } from "@/components/DiscountBox";
import { Heart } from "lucide-react";
import { toast } from "sonner";

// Flip to true to bring back the PayPal option (backend code is still in place).
const PAYPAL_ENABLED = false;

function formatPrice(p, currency = "gbp") {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency.toUpperCase() }).format(p);
  } catch {
    return `£${Number(p).toFixed(2)}`;
  }
}

export default function ImageDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addItem, hasItem } = useCart();
  const { toggleFav, isFav } = useFavourites();
  const navigate = useNavigate();
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState("stripe");
  const [submitting, setSubmitting] = useState(false);
  const [discount, setDiscount] = useState(null);
  const payable = discount ? discount.total_after : image?.price;
  // Siblings in the same class/event so buyers can arrow-key through the
  // gallery without bouncing back to the group page.
  const [siblings, setSiblings] = useState([]);
  const [siblingsKey, setSiblingsKey] = useState(""); // `${event_id}:${group_id}` — cache guard
  const inBasket = image ? hasItem(image.id) : false;

  useEffect(() => {
    setLoading(true);
    api.get(`/images/${id}`).then((r) => setImage(r.data)).catch(() => setImage(null)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (user && user.email) setEmail(user.email);
  }, [user]);

  // Fetch siblings only when the event+group combo changes, not on every
  // photo nav — moving to the next image inside the same class reuses cache.
  useEffect(() => {
    if (!image) return;
    const key = `${image.event_id || ""}:${image.group_id || ""}`;
    if (key === siblingsKey && siblings.length > 0) return;
    const params = {};
    if (image.event_id) params.event_id = image.event_id;
    if (image.group_id) params.group_id = image.group_id;
    api.get("/images", { params })
      .then((r) => {
        setSiblings(Array.isArray(r.data) ? r.data : []);
        setSiblingsKey(key);
      })
      .catch(() => { /* siblings are a nice-to-have, silently fail */ });
  }, [image, siblingsKey, siblings.length]);

  const currentIdx = image ? siblings.findIndex((s) => s.id === image.id) : -1;
  const prevSibling = currentIdx > 0 ? siblings[currentIdx - 1] : null;
  const nextSibling = currentIdx >= 0 && currentIdx < siblings.length - 1 ? siblings[currentIdx + 1] : null;

  const goPrev = React.useCallback(() => {
    if (prevSibling) navigate(`/image/${prevSibling.id}`);
  }, [prevSibling, navigate]);
  const goNext = React.useCallback(() => {
    if (nextSibling) navigate(`/image/${nextSibling.id}`);
  }, [nextSibling, navigate]);

  // Keyboard: ←/→ to nav. Ignore when a form field is focused so typing your
  // email doesn't jump you to the next photo.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext]);

  const handleBuy = async () => {
    if (!email) {
      toast.error("Please enter your email so we can deliver the download link.");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = method === "stripe" ? "/checkout/stripe" : "/checkout/paypal";
      const { data } = await api.post(endpoint, {
        image_id: id,
        origin_url: window.location.origin,
        buyer_email: email,
        discount_code: discount?.code || null,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="pt-32 text-center text-white/50 text-sm uppercase tracking-[0.3em]">Loading…</div>;
  if (!image) return (
    <div className="pt-32 text-center" data-testid="image-not-found">
      <p className="text-white/50 text-sm uppercase tracking-[0.3em]">This piece is no longer available.</p>
      <button onClick={() => navigate("/")} className="mt-6 text-white underline-offset-4 hover:underline text-sm">Back to gallery</button>
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-12 pb-20" data-testid="image-detail-page">
      <div className="flex items-baseline justify-between mb-8 gap-4 flex-wrap">
        <button
          onClick={() => navigate(-1)}
          className="text-xs uppercase tracking-[0.3em] text-white/50 hover:text-white"
          data-testid="back-button"
        >
          ← Back
        </button>
        {siblings.length > 1 && currentIdx >= 0 && (
          <div className="flex items-center gap-4" data-testid="image-nav-strip">
            <button
              onClick={goPrev}
              disabled={!prevSibling}
              className="text-xs uppercase tracking-[0.3em] text-white/60 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed"
              data-testid="image-nav-prev"
              title="Previous photo (←)"
            >
              ← Prev
            </button>
            <span className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold font-condensed" data-testid="image-nav-counter">
              {currentIdx + 1}<span className="text-white/30"> / {siblings.length}</span>
            </span>
            <button
              onClick={goNext}
              disabled={!nextSibling}
              className="text-xs uppercase tracking-[0.3em] text-white/60 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed"
              data-testid="image-nav-next"
              title="Next photo (→)"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-12 lg:gap-16">
        <div className="bg-[#0a0a0a] relative group">
          <img
            src={thumbUrl(image.id)}
            alt={image.title}
            className="w-full h-auto object-contain max-h-[80vh]"
            data-testid="image-preview"
          />
          {/* Overlay chevrons — only visible on desktop hover, so mobile still uses the top strip */}
          {prevSibling && (
            <button
              onClick={goPrev}
              className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 bg-black/60 hover:bg-[var(--pm-orange)] text-white hover:text-black opacity-0 group-hover:opacity-100 transition"
              aria-label="Previous photo"
              data-testid="image-nav-prev-overlay"
            >
              <span className="text-2xl leading-none">‹</span>
            </button>
          )}
          {nextSibling && (
            <button
              onClick={goNext}
              className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 bg-black/60 hover:bg-[var(--pm-orange)] text-white hover:text-black opacity-0 group-hover:opacity-100 transition"
              aria-label="Next photo"
              data-testid="image-nav-next-overlay"
            >
              <span className="text-2xl leading-none">›</span>
            </button>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 self-start space-y-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">Match Photograph</p>
            <h1 className="font-serif text-4xl md:text-5xl font-light leading-none" data-testid="image-title">
              {image.title}
            </h1>
            {image.description && (
              <p className="text-white/65 mt-6 leading-relaxed">{image.description}</p>
            )}
            {(image.race_numbers || []).length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-5" data-testid="image-race-numbers">
                <span className="text-xs uppercase tracking-[0.25em] text-white/40">Spotted</span>
                {image.race_numbers.map((n) => (
                  <Link
                    key={n}
                    to={`/find-my-number?n=${n}`}
                    className="font-display text-sm tracking-wider px-2.5 py-1 bg-[var(--pm-orange)]/15 pm-orange border border-[var(--pm-orange)]/30 hover:bg-[var(--pm-orange)] hover:text-black transition-colors"
                    title={`See more photos of #${n}`}
                    data-testid={`image-number-${n}`}
                  >
                    #{n}
                  </Link>
                ))}
                <span className="text-[11px] text-white/35 font-condensed">tap a number for more of that rider</span>
              </div>
            )}
          </div>

          <div className="border-y border-white/10 py-6 flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-[0.3em] text-white/40">Price</span>
            <span className="font-serif text-4xl" data-testid="image-price">
              {discount ? (
                <>
                  <span className="text-xl text-white/40 line-through mr-3" data-testid="image-list-price">{formatPrice(image.price, image.currency)}</span>
                  <span className="pm-orange">{formatPrice(payable, image.currency)}</span>
                </>
              ) : formatPrice(image.price, image.currency)}
            </span>
          </div>

          <div className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-2">Email for delivery</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="input-minimal"
                data-testid="buyer-email-input"
              />
            </div>

            <DiscountBox imageIds={[image.id]} currency={image.currency} applied={discount} onApplied={setDiscount} />

            {PAYPAL_ENABLED && (
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-3">Payment method</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setMethod("stripe")}
                    className={`py-4 text-xs uppercase tracking-[0.2em] border transition ${
                      method === "stripe" ? "bg-white text-black border-white" : "border-white/20 text-white/70 hover:border-white/60"
                    }`}
                    data-testid="payment-method-stripe"
                  >
                    Card · Stripe
                  </button>
                  <button
                    onClick={() => setMethod("paypal")}
                    className={`py-4 text-xs uppercase tracking-[0.2em] border transition ${
                      method === "paypal" ? "bg-white text-black border-white" : "border-white/20 text-white/70 hover:border-white/60"
                    }`}
                    data-testid="payment-method-paypal"
                  >
                    PayPal
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={handleBuy}
              disabled={submitting}
              className="w-full bg-white text-black py-5 text-xs uppercase tracking-[0.3em] font-medium hover:bg-white/90 transition disabled:opacity-50"
              data-testid="buy-now-button"
            >
              {submitting ? "Preparing checkout…" : `Purchase · ${formatPrice(payable, image.currency)}`}
            </button>

            {inBasket ? (
              <Link
                to="/cart"
                className="w-full block text-center border-2 border-[var(--pm-orange)] pm-orange py-4 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[var(--pm-orange)] hover:text-black transition"
                data-testid="cart-view-button"
              >
                In basket · View basket
              </Link>
            ) : (
              <button
                onClick={() => addItem(image)}
                className="w-full border-2 border-[var(--pm-orange)] pm-orange py-4 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[var(--pm-orange)] hover:text-black transition"
                data-testid="add-to-cart-button"
              >
                + Add to basket
              </button>
            )}

            <button
              onClick={() => toggleFav(image)}
              className={`w-full flex items-center justify-center gap-3 border py-4 text-xs uppercase tracking-[0.3em] font-semibold transition ${
                isFav(image.id)
                  ? "border-[var(--pm-orange)] bg-[var(--pm-orange)]/10 pm-orange"
                  : "border-white/20 text-white/70 hover:border-[var(--pm-orange)] hover:text-[var(--pm-orange)]"
              }`}
              data-testid="fav-toggle-button"
            >
              <Heart size={15} fill={isFav(image.id) ? "currentColor" : "none"} />
              {isFav(image.id) ? "In your favourites" : "Save to favourites"}
            </button>

            <p className="text-xs text-white/40 leading-relaxed">
              Instant download after payment. Full-resolution unwatermarked file for personal use — print it, frame it, share it.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
